import { UpdateWidget } from 'modules/Editor/store/infographSlice.types';
import { WidgetId } from 'types/idTypes';
import { WidgetType } from 'types/widget.types';
import { shuffle } from 'utils/array';
import {
  generatePaletteFromColor,
  getFilteredPalette,
  getReadableTextColorFromList,
  isGrayScale,
  isLight,
  isReadable,
  isTransparent,
  maintainAlpha,
} from 'utils/color';
import { BasicShapeWidgetData } from 'widgets/BasicShapeWidget/BasicShapeWidget.types';
import { TableWidgetData } from 'widgets/TextBasedWidgets/TableWidget/TableWidget.types';
import { TextWidgetData } from 'widgets/TextBasedWidgets/TextWidget/TextWidget.types';
import { GET_WIDGET_COLOR_FN_MAP, SET_WIDGET_COLOR_FN_MAP } from 'widgets/Widget.config';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { AllWidgetData, WidgetMap } from 'widgets/Widget.types';

/**
 * Get the background colour for a widget
 * Returns the new colour of the first intersecting widget of `widgetId`
 *
 * TODO - handle image background case
 *
 * @param widgetId
 * @param widgetMap
 * @param newWidgetColors map of widgetId to it's new color { [widgetId]: color }
 * @returns
 */
export const getBackgroundColorForWidget = (
  widgetId: WidgetId,
  widgetMap: WidgetMap,
  newWidgetColors: { [key: WidgetId]: string },
) => {
  const { topPx, leftPx, heightPx, widthPx } = widgetMap[widgetId];

  // Find intersecting widget
  // If widget doesn't have an assigned new colour, skip it
  // TODO - for image backgrounds, need to get colour of image
  // Return null if no widget intersects - in that case, calling fn should use page colour
  let bgColor: string | null | undefined = null;
  for (const wId in widgetMap) {
    if (wId === widgetId) continue;
    const { topPx: wTopPx, leftPx: wLeftPx, heightPx: wHeightPx, widthPx: wWidthPx } = widgetMap[wId];

    const bottomBound = wTopPx + wHeightPx;
    const rightBound = wLeftPx + wWidthPx;

    if (topPx >= bottomBound) continue;
    if (leftPx >= rightBound) continue;
    if (wTopPx >= topPx + heightPx) continue;
    if (wLeftPx >= leftPx + widthPx) continue;

    const widgetType = getWidgetTypeFromId(wId);
    if (widgetType === WidgetType.Text) continue;

    // Ignore transparent shapes - these are most likely just borders
    if (widgetType === WidgetType.BasicShape) {
      const basicShapeWidgetData = widgetMap[wId] as BasicShapeWidgetData;

      if (isTransparent(basicShapeWidgetData.fillColor[0])) {
        continue;
      }
    }

    bgColor = newWidgetColors[wId];
    if (bgColor) break;
  }
  return bgColor;
};

/**
 * Function to create colour mapping between the colours on the page and the colours in the palette
 * If there are more colours on the page than in the palette, generate more colours
 *
 * @param pageColors
 * @param palette
 * @returns color mapping from page colours to new palette
 * { pageColor1: paletteColor1, pageColor2: paletteColor2, ... }
 */
const createColorMapping = (pageColors: string[], palette: string[]) => {
  const newPaletteColors = [...palette];
  shuffle(newPaletteColors);

  if (pageColors.length > newPaletteColors.length) {
    // Generate more colours if we don't have enough in the palette
    const numNewColors = pageColors.length - newPaletteColors.length;
    for (let i = 0; i < numNewColors; i++) {
      const baseColor = newPaletteColors[i % newPaletteColors.length];
      const fallbackColors = getFilteredPalette(generatePaletteFromColor(baseColor));
      newPaletteColors.push(...fallbackColors);
    }
  }

  // Shuffle new palette colours
  const uniquePaletteColors = [...new Set(newPaletteColors)];
  shuffle(uniquePaletteColors);

  const colorMapping: { [key: string]: string } = {};
  pageColors.forEach((color, idx) => {
    colorMapping[color] = uniquePaletteColors[idx];
  });

  return colorMapping;
};

/**
 * Function to create colour mapping between the colours on chart or multi color widgets and the colours in the palette
 * If there are more colours on chart or multi color widgets than in the palette, generate more colours
 *
 * @param widgetColor
 * @param palette
 * @returns color mapping for array of colors such as chart widgets
 * [color1, color2, color3]
 */
const createMultiColorMapping = (widgetColor: string[], palette: string[]) => {
  const newPaletteColors = [...palette];
  shuffle(newPaletteColors);

  if (widgetColor.length > newPaletteColors.length) {
    // Generate more colours if we don't have enough in the palette
    const numNewColors = widgetColor.length - newPaletteColors.length;
    for (let i = 0; i < numNewColors; i++) {
      const baseColor = newPaletteColors[i % newPaletteColors.length];
      const fallbackColors = generatePaletteFromColor(baseColor);
      newPaletteColors.push(...fallbackColors);
    }
  }

  // Replace colors in widgetColor with those in newPaletteColors
  const updatedWidgetColors = widgetColor.map((_, index) => newPaletteColors[index % newPaletteColors.length]);
  shuffle(updatedWidgetColors);

  return updatedWidgetColors;
};
/**
 * Shuffle the colours in the widget map while ensuring that all text widgets are readable.
 *
 * @param widgetMap Widgets to shuffle
 * @param pageColor
 * @param palette Palette to use when shuffling colours
 * @returns {
 *  widgetUpdates: UpdateWidget[] // can be used when calling updateWidget action,
 *  newPageColor: string,
 * }
 */
export const shuffleColors = (
  widgetMap: WidgetMap,
  pageColor: string,
  palette: string[],
): {
  widgetUpdates: UpdateWidget[];
  newPageColor: string;
} => {
  // Separate palette into light vs dark colours
  // Will be used to update text widgets
  const lightColors: string[] = [];
  const darkColors: string[] = [];
  for (const colorStr of palette) {
    if (isLight(colorStr)) {
      lightColors.push(colorStr);
    } else {
      darkColors.push(colorStr);
    }
  }

  // Get all colours on page
  let pageColors: string[] = [pageColor];
  const originalWidgetColorMap: { [key: WidgetId]: string } = {};

  for (const widgetId in widgetMap) {
    const widgetType = getWidgetTypeFromId(widgetId);
    const getWidgetColorFn = GET_WIDGET_COLOR_FN_MAP[widgetType];
    if (getWidgetColorFn) {
      const widgetColor = getWidgetColorFn(widgetMap[widgetId] as AllWidgetData);

      if (typeof widgetColor !== 'string') continue;
      originalWidgetColorMap[widgetId] = widgetColor;
      pageColors.push(widgetColor);
    }
  }

  // Remove duplicates
  pageColors = [...new Set(pageColors)];

  // Create colour mapping based on colour palette
  // Each colour on the page will be assigned a colour
  const colorMap = createColorMapping(
    pageColors,
    palette.filter((clr) => !!clr),
  );

  const newPageColor = colorMap[pageColor];

  // Store widget ids and their new color
  const widgetToNewColorMap: { [key: WidgetId]: string } = {};

  // Update NON-TEXT widgets with the new colours based on colour mapping
  const widgetUpdates: UpdateWidget[] = [];
  const textWidgets: { [key: WidgetId]: TextWidgetData } = {};
  const shapeWidgets: { [key: WidgetId]: BasicShapeWidgetData } = {};
  const tableWidgets: { [key: WidgetId]: TableWidgetData } = {};
  const excludedWidgetTypes = [WidgetType.BasicShape, WidgetType.Text, WidgetType.Table];

  for (const widgetId in widgetMap) {
    const widgetType = getWidgetTypeFromId(widgetId);

    // Text will be calculated afterwards to ensure readability
    if (widgetType === WidgetType.Text) {
      textWidgets[widgetId] = widgetMap[widgetId] as TextWidgetData;
    }
    // Shape is calculated later
    if (widgetType === WidgetType.BasicShape) {
      shapeWidgets[widgetId] = widgetMap[widgetId] as BasicShapeWidgetData;
    }
    // Table is calculated later
    if (widgetType === WidgetType.Table) {
      tableWidgets[widgetId] = widgetMap[widgetId] as TableWidgetData;
    }

    const getWidgetColorFn = GET_WIDGET_COLOR_FN_MAP[widgetType];
    if (!getWidgetColorFn) continue;

    const isNotShapeAndText = !excludedWidgetTypes.includes(widgetType);

    const widgetColor = getWidgetColorFn(widgetMap[widgetId] as AllWidgetData);
    if (typeof widgetColor !== 'string') {
      const newColorArray = createMultiColorMapping(
        widgetColor,
        palette.filter((clr) => !!clr),
      );

      if (newColorArray && isNotShapeAndText) {
        const setColorFn = SET_WIDGET_COLOR_FN_MAP[widgetType];
        if (setColorFn) {
          widgetUpdates.push({
            widgetId,
            widgetData: setColorFn(widgetMap[widgetId] as AllWidgetData, newColorArray),
          });
        }
      }
    } else {
      const newColor = colorMap[widgetColor];

      if (newColor && isNotShapeAndText) {
        widgetToNewColorMap[widgetId] = newColor;
        const setColorFn = SET_WIDGET_COLOR_FN_MAP[widgetType];
        if (setColorFn) {
          const oldColor = originalWidgetColorMap[widgetId];
          widgetUpdates.push({
            widgetId,
            widgetData: setColorFn(widgetMap[widgetId] as AllWidgetData, maintainAlpha(oldColor, newColor)),
          });
        }
      }
    }
  }

  // Update TEXT widgets with the new colours based on colour mapping new colour should be readable against bg
  // If bg behind text is light - select text from dark colours
  // If bg behind text is dark - select text from light colours
  for (const widgetId in textWidgets) {
    const textWidgetData = textWidgets[widgetId];
    const widgetColor = GET_WIDGET_COLOR_FN_MAP[WidgetType.Text](textWidgetData);

    if (typeof widgetColor !== 'string') continue;
    let newColor = colorMap[widgetColor];

    const isGrayScaleColor = isGrayScale(widgetColor.toLowerCase());

    // Get background colour of intersecting widget - if no widget found, assume background is page colour
    const bgColor = getBackgroundColorForWidget(widgetId, widgetMap, widgetToNewColorMap) || newPageColor;

    const isNewClrReadable = isReadable(bgColor, newColor);
    if (!isNewClrReadable) {
      const textColorOptions = isLight(bgColor) ? shuffle(darkColors) : shuffle(lightColors);
      newColor = getReadableTextColorFromList(bgColor, textColorOptions);
    }

    if (newColor) {
      // restrict color changing for text neutral colors
      const adjustedWidgetColor = isGrayScaleColor ? widgetColor : newColor;
      widgetUpdates.push({
        widgetId,
        widgetData: SET_WIDGET_COLOR_FN_MAP[WidgetType.Text](textWidgetData, adjustedWidgetColor),
      });
    }
  }

  // Update Shape widgets
  for (const widgetId in shapeWidgets) {
    const shapeWidgetData = shapeWidgets[widgetId];
    const widgetColor = GET_WIDGET_COLOR_FN_MAP[WidgetType.BasicShape](shapeWidgetData);

    if (typeof widgetColor !== 'string') continue;

    let newShapeColor = colorMap[widgetColor];
    const isGrayScaleColor = isGrayScale(widgetColor.toLowerCase());

    if (newShapeColor) {
      // restrict color changing for text neutral colors
      const adjustedWidgetColor = isGrayScaleColor ? widgetColor : newShapeColor;
      widgetUpdates.push({
        widgetId,
        widgetData: SET_WIDGET_COLOR_FN_MAP[WidgetType.BasicShape](shapeWidgetData, adjustedWidgetColor),
      });
    }
  }

  // update for table widget data
  for (const widgetId in tableWidgets) {
    const tableWidgetData = tableWidgets[widgetId];

    const widgetColors = GET_WIDGET_COLOR_FN_MAP[WidgetType.Table](tableWidgetData);
    if (typeof widgetColors === 'string') continue;

    // Create a custom color mapping for all table widgets
    const tableColorMap = createColorMapping(
      widgetColors,
      palette.filter((clr) => !!clr),
    );

    if (tableColorMap) {
      widgetUpdates.push({
        widgetId,
        widgetData: SET_WIDGET_COLOR_FN_MAP[WidgetType.Table](tableWidgetData, tableColorMap), // passed the color mapping for color update
      });
    }
  }

  return {
    widgetUpdates,
    newPageColor,
  };
};
